import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "heading", "candidatesList"]

  connect() {
    this.resolveCallback = null
  }

  show(candidates, entityType) {
    return new Promise((resolve) => {
      this.resolveCallback = resolve

      const headings = {
        author: "Which author did you mean?",
        book: "Which book did you mean?",
        publisher: "Which publisher did you mean?"
      }

      if (this.hasHeadingTarget) {
        this.headingTarget.textContent = headings[entityType] || "Which did you mean?"
      }

      this.candidatesListTarget.innerHTML = candidates.map((candidate, index) => {
        return this.buildCandidateCard(candidate, entityType, index)
      }).join("")

      this.dialogTarget.showModal()
      document.body.style.overflow = "hidden"
    })
  }

  buildCandidateCard(candidate, entityType, index) {
    if (entityType === "author") {
      return this.buildAuthorCard(candidate, index)
    } else if (entityType === "book") {
      return this.buildBookCard(candidate, index)
    } else if (entityType === "publisher") {
      return this.buildPublisherCard(candidate, index)
    }
    return ""
  }

  buildAuthorCard(candidate, index) {
    const name = this.escapeHtml(candidate.name || "Unknown")
    const years = this.formatYears(candidate.birth_year, candidate.death_year)
    const nationality = candidate.nationality ? this.escapeHtml(candidate.nationality) : ""
    const description = candidate.description ? this.escapeHtml(candidate.description) : ""
    const works = candidate.notable_works ? this.escapeHtml(candidate.notable_works) : ""

    let details = ""
    if (nationality) details += `<span class="text-beige-600">${nationality}</span>`
    if (nationality && description) details += ` · `
    if (description) details += `<span class="text-gray-600">${description}</span>`

    let worksHtml = ""
    if (works) {
      worksHtml = `<div class="mt-1 text-xs text-beige-500">Notable: ${works}</div>`
    }

    return `
      <button type="button"
              data-action="ai-disambiguation#selectCandidate"
              data-candidate-index="${index}"
              class="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all cursor-pointer mb-3 last:mb-0 group">
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-gray-900 group-hover:text-amber-900">
            ${name}${years ? ` <span class="text-sm font-normal text-beige-500">(${years})</span>` : ""}
          </div>
          <div class="text-sm mt-0.5">${details}</div>
          ${worksHtml}
        </div>
      </button>
    `
  }

  buildBookCard(candidate, index) {
    const title = this.escapeHtml(candidate.title || "Unknown")
    const author = candidate.author ? this.escapeHtml(candidate.author) : ""
    const year = candidate.publication_year || ""
    const description = candidate.description ? this.escapeHtml(candidate.description) : ""

    let byline = ""
    if (author) byline += `by ${author}`
    if (author && year) byline += ` `
    if (year) byline += `(${year})`

    return `
      <button type="button"
              data-action="ai-disambiguation#selectCandidate"
              data-candidate-index="${index}"
              class="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all cursor-pointer mb-3 last:mb-0 group">
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-gray-900 group-hover:text-amber-900">"${title}"</div>
          ${byline ? `<div class="text-sm text-beige-600 mt-0.5">${byline}</div>` : ""}
          ${description ? `<div class="text-sm text-gray-600 mt-1">${description}</div>` : ""}
        </div>
      </button>
    `
  }

  buildPublisherCard(candidate, index) {
    const name = this.escapeHtml(candidate.name || "Unknown")
    const location = candidate.location ? this.escapeHtml(candidate.location) : ""
    const founded = candidate.founded_year || ""
    const description = candidate.description ? this.escapeHtml(candidate.description) : ""

    let meta = ""
    if (location) meta += location
    if (location && founded) meta += ` · `
    if (founded) meta += `Founded ${founded}`

    return `
      <button type="button"
              data-action="ai-disambiguation#selectCandidate"
              data-candidate-index="${index}"
              class="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all cursor-pointer mb-3 last:mb-0 group">
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-gray-900 group-hover:text-amber-900">${name}</div>
          ${meta ? `<div class="text-sm text-beige-600 mt-0.5">${meta}</div>` : ""}
          ${description ? `<div class="text-sm text-gray-600 mt-1">${description}</div>` : ""}
        </div>
      </button>
    `
  }

  formatYears(birth, death) {
    if (!birth && !death) return ""
    if (birth && death) return `${birth} – ${death}`
    if (birth) return `b. ${birth}`
    if (death) return `d. ${death}`
    return ""
  }

  selectCandidate(event) {
    const index = parseInt(event.currentTarget.dataset.candidateIndex, 10)
    this.close()
    if (this.resolveCallback) {
      this.resolveCallback(index)
      this.resolveCallback = null
    }
  }

  cancel() {
    this.close()
    if (this.resolveCallback) {
      this.resolveCallback(null)
      this.resolveCallback = null
    }
  }

  close() {
    this.dialogTarget.close()
    document.body.style.overflow = ""
  }

  backdropClick(event) {
    if (event.target === this.dialogTarget) {
      this.cancel()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.dialogTarget.open) {
      event.preventDefault()
      this.cancel()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
